'use client';

import { twBreakpoints, WoongebouwCard } from '@woonstad/design-system';
import { useMediaQuery } from '@woonstad/helpers-hooks';
import { trackEvent } from '@woonstad/modules-google-tag-manager';
import { getDictionary, mediaUrlForNextImage, OverviewItemSlotProps, SITES, WoongebouwenResponse } from '@woonstad/ui-public-sites';
import Image from 'next/image';
import Link from 'next/link';

type Props = OverviewItemSlotProps<NonNullable<WoongebouwenResponse['items']>[number]>

function getAvailabilityTag(available: number | undefined, dict: ReturnType<typeof getDictionary>) {
  if (!available) {
    return { type: 'warning' as const, text: dict['Geen woningen beschikbaar'] };
  }
  const label = available === 1 ? dict['woning beschikbaar'] : dict['woningen beschikbaar'];
  return { type: 'success' as const, text: `${available} ${label}` };
}

export const WoongebouwItem = ({ item: woongebouw }: Props) => {
  const dict = getDictionary(SITES.Stadswonen);

  const isMobile = useMediaQuery(twBreakpoints['max-sm']);

  const handleClickAdvert = () => {
    trackEvent({
      event: 'select_woongebouw',
      item_locatie: 'Woongebouw overzichtspagina',
      woongebouw_naam: woongebouw.title ?? '',
      doelgroep: woongebouw.target ?? '',
      woongebouw_regio: woongebouw.region ?? '',
      woongebouw_district: woongebouw.district ?? '',
      available: woongebouw.available,
    });
  };

  const cardImageSrc = mediaUrlForNextImage(woongebouw.image?.url) ?? '/static/image-fallback.jpg';

  return (
    <WoongebouwCard
      image={
        <Image
          src={cardImageSrc}
          alt={woongebouw.image?.description ?? 'Geen afbeelding beschikbaar'}
          width={300}
          height={200}
          sizes={woongebouw.image?.url ? `${twBreakpoints['max-sm']} 70vw, 250px` : undefined}
          priority={false}
        />
      }
      address={woongebouw.title ?? ''}
      subaddress={woongebouw.region ?? ''}
      tags={[getAvailabilityTag(woongebouw.available, dict)]}
      variant={isMobile ? 'square' : 'landscape'}
      description={woongebouw.description ?? ''}
      facilities={woongebouw.facilityList
        ?.filter((facility) => Boolean(facility.name))
        ?.map((facility) => ({
          type: facility.key ?? '',
          title: facility.name as string,
          description: facility.description ?? undefined,
        }))}
    >
      <Link
        onClick={handleClickAdvert}
        href={`/woongebouwen/${woongebouw.url}`}
      />
    </WoongebouwCard>
  );
};
